import { PORTAL_RETRIEVAL_MESSAGES } from 'components/inboundReferrals/constants';

// The window.postMessage handshake with the Olio browser extension. The
// extension's content script listens on our own page's window: we post a job, it acks
// immediately (or it isn't installed), then posts the result whenever the retrieval
// finishes — minutes later for a recording a human drives.

const ACK_TIMEOUT_MS = 500;

export interface PortalRetrievalJob {
  // Replay: the saved script to execute. Absent for record-mode jobs.
  script?: { entryUrl: string; body: string; retrievalUrl?: string | null };
  // Where the extension opens the portal tab — the referral's live retrieval
  // pointer, for record AND replay. The script's stored entryUrl is record-time
  // (stale referral ids) and is only the extension's fallback when this is absent.
  entryUrl?: string;
  record?: boolean;
  // AI-recording mode: the extension's side-panel conductor drives the turns against
  // this run, capturing the AI's actions as the recorded script.
  ai?: boolean;
  runId?: string;
  goal?: { outputs: string[]; instructions?: string };
  availableParams?: Record<string, string>;
  params: Record<string, unknown>;
  context: Record<string, unknown>;
  expectedOutputs: string[];
  // Names in expectedOutputs that are documents: their marks record as `download`
  // statements, and their values come back as base64 file entries (see
  // services/portalDocuments for the upload hand-off).
  documentOutputs?: string[];
}

interface ExtensionMessage {
  type?: string;
  jobId?: string;
  launchId?: string;
  probe?: boolean;
  ok?: boolean;
  values?: Record<string, unknown>;
  error?: string;
  status?: string;
}

// Live (non-terminal) states the conductor broadcasts while a job runs. waiting_login
// and confused halt the automation without ending the job — the result still arrives
// later on the result message.
export type PortalRetrievalStatus = 'running' | 'waiting_login' | 'confused';

export interface RunPortalRetrievalOptions {
  // Called on every status broadcast for the job. jobId is included so the caller can
  // restart a confused job (restartPortalRetrieval) while the run promise stays pending.
  onStatus?: (status: PortalRetrievalStatus, jobId: string) => void;
}

// Is the extension installed on this browser? It answers the probe instantly, so a
// short timeout is a reliable "not installed".
export function probePortalExtension(timeoutMs = 500): Promise<boolean> {
  return new Promise((resolve) => {
    const onMessage = (event: MessageEvent<ExtensionMessage>) => {
      if (event.source !== window) return;
      if (event.data?.type !== PORTAL_RETRIEVAL_MESSAGES.ack || !event.data.probe) return;
      clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve(true);
    };
    const timer = setTimeout(() => {
      window.removeEventListener('message', onMessage);
      resolve(false);
    }, timeoutMs);
    window.addEventListener('message', onMessage);
    window.postMessage({ type: PORTAL_RETRIEVAL_MESSAGES.probe }, window.location.origin);
  });
}

// Run one retrieval job. Rejects fast when the extension never acks; after the ack
// there is deliberately NO timeout — a record-mode job runs as long as the human takes.
// Status broadcasts (waiting_login, confused) surface through options.onStatus and never
// settle the promise — even a confused job keeps running until its result arrives.
export function runPortalRetrieval(
  job: PortalRetrievalJob,
  options?: RunPortalRetrievalOptions
): Promise<Record<string, unknown>> {
  return new Promise((resolve, reject) => {
    const jobId = crypto.randomUUID();

    const onMessage = (event: MessageEvent<ExtensionMessage>) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || data.jobId !== jobId) return;

      if (data.type === PORTAL_RETRIEVAL_MESSAGES.ack) {
        clearTimeout(ackTimer);
        return;
      }
      if (data.type === PORTAL_RETRIEVAL_MESSAGES.status) {
        if (data.status) options?.onStatus?.(data.status as PortalRetrievalStatus, jobId);
        return;
      }
      if (data.type === PORTAL_RETRIEVAL_MESSAGES.result) {
        clearTimeout(ackTimer);
        window.removeEventListener('message', onMessage);
        if (data.ok) resolve(data.values ?? {});
        else reject(new Error(data.error || 'Portal retrieval failed.'));
      }
    };

    const ackTimer = setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('Olio browser extension not detected.'));
    }, ACK_TIMEOUT_MS);

    window.addEventListener('message', onMessage);
    window.postMessage({ type: PORTAL_RETRIEVAL_MESSAGES.request, jobId, ...job }, window.location.origin);
  });
}

// Restart a confused job from scratch — the extension's conductor reruns the state
// machine in the same tab from the entry URL. The original run promise stays pending;
// the (re)run's outcome arrives on the same result message.
export function restartPortalRetrieval(jobId: string) {
  window.postMessage({ type: PORTAL_RETRIEVAL_MESSAGES.restart, jobId }, window.location.origin);
}

// Render metadata for one HUD entry field — the same shape the Olio card renders from
// (ReferralFieldDef + options). The extension renders whatever it's handed: 'date'
// becomes a native date input (values arrive as YYYY-MM-DD), 'select' a dropdown over
// `options`, anything else a text input. The typing is Olio's alone.
export interface ManualPortalField {
  name: string;
  label: string;
  type: 'text' | 'date' | 'select';
  options?: { value: string; label: string }[];
}

export interface ManualPortalLaunch {
  entryUrl: string;
  // The names of the values the extension's in-page HUD offers entry fields for. The
  // extension renders whatever names it's handed — this list is Olio's alone.
  expectedOutputs: string[];
  // Per-name render/validation metadata for the HUD's entry fields.
  fields?: ManualPortalField[];
  params?: Record<string, unknown>;
  context?: Record<string, unknown>;
}

// Manual mode (OLIO-2703): ask the extension to open the portal in a new tab the user
// drives by hand (no script). The launched tab gets a manual-entry HUD for the
// expectedOutputs; whatever the person types there arrives on the shared result channel
// keyed by launchId (see onManualPortalResult). The extension owns the tab's lifecycle
// and closes it on finishManualPortal (OLIO-2704) or on the HUD's own send, so it
// returns a launchId to track by. Rejects if the extension never acks (not launched).
export function launchManualPortal(
  launch: ManualPortalLaunch,
  timeoutMs = ACK_TIMEOUT_MS
): Promise<{ launchId: string }> {
  return new Promise((resolve, reject) => {
    const launchId = crypto.randomUUID();
    const onMessage = (event: MessageEvent<ExtensionMessage>) => {
      if (event.source !== window) return;
      const data = event.data;
      if (data?.type !== PORTAL_RETRIEVAL_MESSAGES.manualLaunchAck || data.launchId !== launchId) return;
      clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      if (data.ok) resolve({ launchId });
      else reject(new Error(data.error || 'Could not open the portal tab.'));
    };
    const timer = setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('Olio browser extension not detected.'));
    }, timeoutMs);
    window.addEventListener('message', onMessage);
    window.postMessage({ type: PORTAL_RETRIEVAL_MESSAGES.manualLaunch, launchId, ...launch }, window.location.origin);
  });
}

// The manual launch's result: ok + typed values from the HUD's send, or ok:false when
// the person closed the tab without sending (values then carry any drafts they'd
// typed). One-shot; returns the unsubscribe for effect cleanup.
export function onManualPortalResult(
  launchId: string,
  handler: (result: { ok: boolean; values: Record<string, unknown> }) => void
): () => void {
  const onMessage = (event: MessageEvent<ExtensionMessage>) => {
    if (event.source !== window) return;
    const data = event.data;
    if (data?.type !== PORTAL_RETRIEVAL_MESSAGES.result || data.jobId !== launchId) return;
    window.removeEventListener('message', onMessage);
    handler({ ok: !!data.ok, values: data.values ?? {} });
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

// Close a tab opened by launchManualPortal (fire-and-forget). The extension closes only
// tabs it opened, never one the user opened themselves.
export function finishManualPortal(launchId: string) {
  window.postMessage({ type: PORTAL_RETRIEVAL_MESSAGES.manualClose, launchId }, window.location.origin);
}
